import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UserRole } from '@authentication/model/auth-user.model';
import { AuthService } from '@authentication/services/auth.service';
import { isLicenseNotSet, isSubscriptionLicenseType } from '@core/helpers/license/license.helper';
import { FarmDeviceService } from '@core/services/farm-device/farm-device.service';
import { FarmsService } from '@core/services/farms/farms.service';
import { LicenseService } from '@core/services/license/license.service';
import { CustomerType } from '@license/invoice-details-form/customer-type.enum';
import { License } from '@license/model/license.model';
import { ChangePasswordModalComponent } from '@shared/component/my-account/change-password-modal/change-password-modal.component';
import { AddDeviceDialogComponent } from '@shared/component/my-account/devices/add-device-dialog/add-device-dialog.component';
import { UserDataType } from '@shared/component/my-account/model/user-data-type.enum';
import { MyAccountStoreService } from '@shared/component/my-account/my-account-store.service';
import { UserDataModalComponent } from '@shared/component/my-account/user-data-modal/user-data-modal.component';
import { FarmDevice } from '@shared/model/farm-device.model';
import { LicenseStatus } from '@shared/model/license-status';
import { LicenseType } from '@shared/model/license-type.enum';
import { UserProfile, UserProfileAddress } from '@shared/model/user-profile.model';
import { resolvePhonePrefix } from '@shared/utils/common.utils';
import { BehaviorSubject, filter, finalize, Observable, of, Subject, switchMap, take, takeUntil, tap } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Component({
	selector: 'sf-my-account',
	templateUrl: './my-account.component.html',
	styleUrls: ['./my-account.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [MyAccountStoreService],
})
export class MyAccountComponent implements OnDestroy {
	private readonly refreshDevices: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);

	public myAccountData$: Observable<UserProfile> = this.myAccountStore.myAccountData$;
	public license$: Observable<License> = this.licenseService.license$;
	public billingPortalUrl$: Observable<string | null> = this.license$.pipe(
		map((license) => license?.subscription?.billingPortalUrl ?? null)
	);
	public showHomeLink$ = this.authService.activeZone.pipe(map((zone) => zone === null));
	// catchError sits on the INNER observable: an error escaping switchMap would terminate the
	// outer refreshDevices subscription for good, so every later next() after add/disconnect/
	// reissue/delete would silently do nothing until a full page reload.
	public devices$: Observable<FarmDevice[]> = this.refreshDevices.pipe(
		switchMap(() => this.farmDeviceService.list().pipe(catchError(() => of([]))))
	);
	public LicenseType: typeof LicenseType = LicenseType;
	public LicenseStatus: typeof LicenseStatus = LicenseStatus;
	public CustomerType: typeof CustomerType = CustomerType;
	public UserDataType: typeof UserDataType = UserDataType;
	public userActive = this.authService.user.is_active;
	public isSubscriptionLicenseType$ = this.license$.pipe(
		map((license) => isSubscriptionLicenseType(license.licenseType))
	);
	public isLicenseNotSet$ = this.license$.pipe(
		map((license) => isLicenseNotSet(license.licenseType, license.licenseStatus))
	);
	public isLicenseCanceledOrInactive$ = this.license$.pipe(
		map(
			(license) => license.licenseStatus === LicenseStatus.INACTIVE || license.licenseStatus === LicenseStatus.CANCELED
		)
	);
	public isLecturerRole$ = this.myAccountData$.pipe(map((data) => data?.roles?.includes(UserRole.LECTURER)));
	// Device routes are not in the API's demo allowlist (config/demo_user.php), so a DEMO user
	// gets 403 on GET /devices; students have no farms of their own either. Hide the section
	// rather than render one that can only fail.
	public canUseDevices$ = this.myAccountData$.pipe(
		map((data) => !data?.roles?.some((role) => role === UserRole.DEMO || role === UserRole.STUDENT))
	);
	public addDeviceInProgress = false;

	private readonly destroy: Subject<void> = new Subject();

	public get personalAddressData(): UserProfileAddress {
		return this.myAccountData.personalAddress;
	}

	public get invoiceAddressData(): UserProfileAddress {
		return this.myAccountData.invoiceAddress;
	}

	private get myAccountData(): UserProfile {
		return this.myAccountStore.myAccountData;
	}

	constructor(
		private readonly modal: MatDialog,
		private readonly myAccountStore: MyAccountStoreService,
		private readonly licenseService: LicenseService,
		private readonly cdr: ChangeDetectorRef,
		private readonly authService: AuthService,
		private readonly farmDeviceService: FarmDeviceService,
		private readonly farmsService: FarmsService
	) {}

	public ngOnDestroy(): void {
		this.destroy.next();
		this.destroy.complete();
	}

	public resolvePrefix(iso: string): string {
		return resolvePhonePrefix(iso);
	}

	public onChangePasswordClick(): void {
		this.modal
			.open(ChangePasswordModalComponent, {
				width: '600px',
				height: 'auto',
				autoFocus: 'auto',
				hasBackdrop: true,
				disableClose: true,
			})
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe();
	}

	public onEditDataClick(dataType: UserDataType): void {
		this.modal
			.open(UserDataModalComponent, {
				width: '600px',
				height: 'auto',
				autoFocus: 'auto',
				hasBackdrop: true,
				disableClose: true,
				data: {
					dataType: dataType,
					formData: { personalAddress: this.personalAddressData, invoiceAddress: this.invoiceAddressData },
				},
			})
			.afterClosed()
			.pipe(
				filter((data: any) => !!data.dataUpdated),
				tap((data) => {
					this.myAccountStore.updateProfileData(data.formData);
				}),
				takeUntil(this.destroy)
			)
			.subscribe();
	}

	public navigateToHome(): void {
		this.authService.navigateAfterLogin();
	}

	public onAddDeviceClick(): void {
		if (this.addDeviceInProgress) {
			return;
		}
		this.addDeviceInProgress = true;
		this.cdr.markForCheck();
		this.farmsService
			.getFarms()
			.pipe(
				take(1),
				finalize(() => {
					this.addDeviceInProgress = false;
					this.cdr.markForCheck();
				}),
				takeUntil(this.destroy)
			)
			.subscribe((farms) => {
				this.modal
					.open(AddDeviceDialogComponent, {
						width: '480px',
						height: 'auto',
						autoFocus: 'auto',
						hasBackdrop: true,
						disableClose: true,
						data: { mode: 'create', farms },
					})
					.afterClosed()
					.pipe(takeUntil(this.destroy))
					.subscribe(() => this.onDevicesChanged());
			});
	}

	public onDevicesChanged(): void {
		this.refreshDevices.next();
	}
}
